import { gettext } from '@eventbrite/i18n';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { Wrapper } from '@googlemaps/react-wrapper';
import React from 'react';
import { GACodes, Location } from '../../types';
import { dynaMount } from '../utils/mountComponent';
import { EVENT_MAP_ID } from '../_shared/constants';
import './EventMap.scss';
import { EventMapProps } from './EventMapRoot';
import {
    buttonsConfig,
    G_MAPS_LINK,
    mapOptions,
    markerOptions,
} from './MapStyles';

interface MapProps extends google.maps.MapOptions {
    style: { [key: string]: string };
}

const getButtonList = (location: Location): JSX.Element[] =>
    buttonsConfig.map((btnConfig, index) => {
        const extraParams = new URLSearchParams(
            btnConfig.queryParams,
        ).toString();
        const link = `${G_MAPS_LINK}&daddr=${location.latitude},${location.longitude}&${extraParams}`;
        const label = btnConfig.label;
        return (
            <li key={index}>
                <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn--target is-collapsed btn--invert"
                    aria-label={btnConfig.ariaLabel}
                    onClick={() =>
                        trackAnalyticsEvent({
                            category: GACodes.GA_CATEGORY_LISTING,
                            action: GACodes.GA_ACTION_DIRECTIONS,
                            label: label,
                        })
                    }
                >
                    <i
                        className={btnConfig.icoClass}
                        style={{ fontWeight: 'normal' }}
                    />
                </a>
            </li>
        );
    });

const EventMapButtons = (location: Location): JSX.Element => {
    return <ul className="grouped-ico">{getButtonList(location)}</ul>;
};

const Map: React.FC<MapProps> = ({ children, style, ...options }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [map, setMap] = React.useState<google.maps.Map>();

    React.useEffect(() => {
        if (ref.current && !map) {
            setMap(new window.google.maps.Map(ref.current, {}));
        }
    }, [ref, map]);

    React.useEffect(() => {
        if (map) {
            map.setOptions(options);
        }
    }, [map, options]);

    React.useEffect(() => {
        if (map) {
            map.addListener('zoom_changed', () => {
                trackAnalyticsEvent({
                    category: GACodes.GA_CATEGORY_LISTING,
                    action: GACodes.GA_ACTION_MAP_INTERACTION,
                    label: GACodes.GA_LABEL_ZOOM,
                });
            });
        }
    }, [map]);

    const cloneChildrenAddingMapProp = () =>
        React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
                return React.cloneElement(child, { map });
            }
        });

    return (
        <>
            <div ref={ref} style={style}></div>
            {cloneChildrenAddingMapProp()}
        </>
    );
};

export const Marker: React.FC<google.maps.MarkerOptions> = (options) => {
    const [marker, setMarker] = React.useState<google.maps.Marker>();

    React.useEffect(() => {
        if (!marker) {
            setMarker(new google.maps.Marker());
        }
    }, [marker]);

    React.useEffect(() => {
        if (marker) {
            marker.setOptions({ ...options });
        }
    }, [marker, options]);

    return null;
};

const MapContainer = ({
    mapZoom,
    location,
    mapOptions,
    markerOptions,
    api_key,
}: {
    mapZoom: number;
    location: Location;
    mapOptions: google.maps.MapOptions;
    markerOptions: google.maps.MarkerOptions;
    api_key: string;
}) => {
    const center: google.maps.LatLngLiteral = {
        lat: location.latitude,
        lng: location.longitude,
    };
    return (
        <div className="event-map__wrapper">
            <Wrapper apiKey={api_key}>
                <Map
                    center={center}
                    zoom={mapZoom}
                    style={{ width: '100%' }}
                    {...mapOptions}
                >
                    <Marker
                        position={{
                            lat: location.latitude,
                            lng: location.longitude,
                        }}
                        {...markerOptions}
                    />
                </Map>
            </Wrapper>
        </div>
    );
};

export const EventMap: React.FunctionComponent<EventMapProps> = ({
    eventName,
    venueName,
    venueAddress,
    mapZoom,
    location,
    gMapsApiKey,
}) => {
    return (
        <section
            className="event-map eds-g-cell--align-center"
            data-testid="event-map-component"
        >
            <h2 className="eds-is-hidden-accessible">
                {gettext('Map and Directions')}
            </h2>
            <div className="event-map__placeholder eds-l-pad-bot-5">
                <MapContainer
                    mapZoom={mapZoom}
                    location={location}
                    mapOptions={mapOptions}
                    markerOptions={markerOptions}
                    api_key={gMapsApiKey}
                />
            </div>
            <div className="event-map__details eds-text--center eds-l-pad-bot-2">
                <div className="eds-l-pad-vert-5">
                    <h2 className="eds-text-bl eds-text-color--secondary-brand eds-l-pad-bot-1">
                        {eventName}
                        <div className="eds-text-color--grey-800 eds-text-bm eds-l-pad-vert-2">
                            {gettext('at')}
                        </div>
                        <span>{venueName}</span>
                    </h2>
                    <p className="eds-text-color--ui-800 eds-text-bm eds-l-pad-top-1">
                        {venueAddress}
                    </p>
                </div>
                <div>
                    <EventMapButtons {...location} />
                </div>
            </div>
        </section>
    );
};

const EventMapMount = (props: EventMapProps): JSX.Element =>
    dynaMount(
        {
            Component: EventMap,
            containerId: EVENT_MAP_ID,
        },
        props,
    );

export default EventMapMount;
